// Story 8.1 (ADO-2010), Task 8.4 — the Foodservice / Licensed Trade / LSN landing page
// (AC10-AC12; A10.2-A10.6).
//
// Where a Trading Account of one of those types lands once its Cart is chosen (A10.1).
// It reads `GET /api/v1/landing?type=` for the active account's type and shows the
// tenant's category tiles; each opens the product list with the tile's `productQuery`
// in the URL (a Foodservice tile carries the removable Catering-only default, A10.4; an
// LSN tile its category, A10.6). The Global Favourites section renders only when the
// read returns some (A10.5: always empty until Story 7.6). A tile has an image when the
// read gives one (the images committed under `public/images/landing/`, 8.1c), otherwise it
// is a text tile.
//
// There is no Figma frame for this page (A10.8): it is composed from the design
// system's header, tokens and Banner and is flagged for design review.
//
// Without an active choice a store user is sent to the choice screen; an account type
// with no landing page (or staff) goes to the product list. A switch of account while
// the page is open re-reads for the new type, and a late reply for the old type is
// dropped.

import { useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router';
import { Banner, Button } from '../../design-system';
import {
  fetchLandingPage,
  productListHref,
  type LandingPage as LandingPageData,
  type LandingTile,
  type LandingType,
} from '../../api/landing.client';
import { useSessionStore } from '../../auth/useSessionStore';
import { isAdminRole } from '../../auth/rbac';
import { CHOOSE_ROUTE, PRODUCTS_ROUTE, landingTypeFor } from '../../ordering/orderingChoices';
import { CatalogueLinkingHeader } from '../Products/CatalogueLinkingHeader';
import styles from './LandingPage.module.css';

const TITLES: Readonly<Record<LandingType, string>> = {
  FOODSERVICE: 'Foodservice',
  LICENSED_TRADE: 'Licensed Trade',
  LSN: 'LSN',
};

type Phase = 'loading' | 'ready' | 'error';

function TileGrid({ tiles }: { readonly tiles: readonly LandingTile[] }) {
  return (
    <ul className={styles.grid}>
      {tiles.map((tile) => (
        <li key={tile.key}>
          <Link
            to={productListHref(tile)}
            className={styles.tile}
            data-image={tile.imageUrl !== null ? 'true' : undefined}
          >
            {tile.imageUrl !== null && (
              <img src={tile.imageUrl} alt="" className={styles.tileImage} loading="lazy" />
            )}
            <span className={styles.tileName}>{tile.name}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function LandingPage() {
  const role = useSessionStore((s) => s.role);
  const choices = useSessionStore((s) => s.orderingChoices);
  const activeEntryKey = useSessionStore((s) => s.activeEntryKey);
  const activeTradingAccount = useSessionStore((s) => s.activeTradingAccount);
  const entryLabel = useSessionStore(
    (s) => s.orderingChoices?.entries.find((entry) => entry.key === s.activeEntryKey)?.label,
  );
  const type = landingTypeFor(activeTradingAccount);

  const [landing, setLanding] = useState<LandingPageData | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [attempt, setAttempt] = useState(0);
  const readSeq = useRef(0);

  useEffect(() => {
    if (type === null) return;
    const seq = ++readSeq.current;
    setPhase('loading');
    setLanding(null);
    fetchLandingPage(type)
      .then((page) => {
        if (seq !== readSeq.current) return;
        setLanding(page);
        setPhase('ready');
      })
      .catch(() => {
        if (seq !== readSeq.current) return;
        setPhase('error');
      });
  }, [type, attempt]);

  if (choices?.scope === 'TRADING_ACCOUNTS' && activeEntryKey === null) {
    return <Navigate to={CHOOSE_ROUTE} replace />;
  }
  if (type === null) return <Navigate to={PRODUCTS_ROUTE} replace />;

  return (
    <div className={styles.page}>
      <CatalogueLinkingHeader showManagerDashboard={isAdminRole(role)} />
      <main className={styles.main} aria-label="Landing page">
        <h1 className={styles.heading}>{TITLES[type]}</h1>
        {entryLabel !== undefined && <p className={styles.subheading}>{entryLabel}</p>}

        {phase === 'loading' && (
          <p role="status" className={styles.status}>
            Loading categories…
          </p>
        )}

        {phase === 'error' && (
          <div className={styles.notice}>
            <Banner type="error">We couldn&rsquo;t load this page. Please try again.</Banner>
            <Button variant="secondary" onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </Button>
          </div>
        )}

        {phase === 'ready' && landing !== null && (
          <>
            {landing.tiles.length > 0 ? (
              <section className={styles.section} aria-labelledby="landing-categories">
                <h2 id="landing-categories" className={styles.sectionTitle}>
                  Shop by category
                </h2>
                <TileGrid tiles={landing.tiles} />
              </section>
            ) : (
              <div className={styles.empty} role="status">
                <p>There are no categories to show here yet.</p>
                <Link to={PRODUCTS_ROUTE} className={styles.allLink}>
                  Browse all products
                </Link>
              </div>
            )}
            {landing.globalFavourites.length > 0 && (
              <section className={styles.section} aria-labelledby="landing-global-favourites">
                <h2 id="landing-global-favourites" className={styles.sectionTitle}>
                  Global Favourites
                </h2>
                <TileGrid tiles={landing.globalFavourites} />
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default LandingPage;
